import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import { CloudUpload, CloudDownload, Delete, Search, Image, VideoFile, AudioFile } from '@mui/icons-material';
import { apiService } from '../shared/services/api';

interface MediaFile {
  file_name: string;
  file_url: string;
  file_size: number;
  last_modified: string;
  key: string;
}

interface MediaFileListResponse {
  files?: MediaFile[];
}

interface MediaFileSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (fileUrl: string) => void;
  fileType: 'image' | 'video' | 'audio';
  currentValue?: string;
  contentId?: number;
  screen?: 'primary' | 'secondary';
}

const MediaFileSelector: React.FC<MediaFileSelectorProps> = ({
  open,
  onClose,
  onSelect,
  fileType,
  currentValue,
  contentId,
  screen,
}) => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.listS3Files(fileType);
      const filesResponse = response as MediaFileListResponse;
      setFiles(filesResponse.files || []);
    } catch (err) {
      setError('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  }, [fileType]);

  useEffect(() => {
    if (open) {
      fetchFiles();
    }
  }, [open, fileType, fetchFiles]);

  useEffect(() => {
    if (open) return;
    setSelectedFile(null);
    setPreviewUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [open]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl('');
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);
      const response = await apiService.uploadContentFile(selectedFile, fileType, contentId, screen);

      const uploadedFile = response as MediaFile;

      // update files list
      setFiles([uploadedFile, ...files]);

      // show success message
      setSuccessMessage(`File "${selectedFile.name}" uploaded successfully!`);

      if (uploadedFile.file_url) {
        setPreviewUrl(uploadedFile.file_url);
      }

      // automatically select the uploaded file and close the dialog
      setTimeout(() => {
        if (uploadedFile.file_url) {
          onSelect(uploadedFile.file_url);
        }
        onClose();
      }, 1000); // delay 1 second to show the success message

      setSelectedFile(null);
    } catch (err) {
      setError(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileKey: string) => {
    try {
      await apiService.deleteS3File(fileKey);
      setFiles(files.filter(f => f.key !== fileKey));
      setSuccessMessage('File deleted successfully');
    } catch (err: unknown) {
      const responseData =
        typeof err === 'object' && err && 'response' in err
          ? (err as { response?: { data?: { detail?: unknown; error?: unknown } } }).response?.data
          : undefined;

      const responseDetail = responseData?.detail;
      const responseError = responseData?.error;
      const messageFromError = err instanceof Error ? err.message : undefined;

      const detailedMsg =
        typeof responseDetail === 'string'
          ? responseDetail
          : typeof responseError === 'string'
            ? responseError
            : messageFromError && messageFromError !== 'Request failed with status code 400'
              ? messageFromError
              : 'Operation failed. The file may be referenced by content and cannot be deleted.';
      setError(detailedMsg);
    }
  };

  const handleSelectFile = (fileUrl: string) => {
    onSelect(fileUrl);
    onClose();
  };

  const handleClearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onSelect('');
  };

  const filteredFiles = files.filter(file => file.file_name.toLowerCase().includes(searchTerm.toLowerCase()));

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = () => {
    switch (fileType) {
      case 'image':
        return <Image />;
      case 'video':
        return <VideoFile />;
      case 'audio':
        return <AudioFile />;
      default:
        return <Image />;
    }
  };

  const renderPreview = () => {
    if (!previewUrl) return null;

    if (fileType === 'image') {
      return (
        <Box
          sx={{
            mt: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            maxWidth: 480,
          }}
        >
          <Box component="img" src={previewUrl} alt="Selected preview" sx={{ width: '100%', display: 'block' }} />
        </Box>
      );
    }

    if (fileType === 'video') {
      return (
        <Box
          sx={{
            mt: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            maxWidth: 640,
          }}
        >
          <Box
            component="video"
            src={previewUrl}
            controls
            sx={{ width: '100%', display: 'block', backgroundColor: 'black' }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Box component="audio" src={previewUrl} controls sx={{ width: '100%' }} />
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Select {fileType.charAt(0).toUpperCase() + fileType.slice(1)} File</DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Upload New File" icon={<CloudUpload />} />
            <Tab label="Choose from Library" icon={<CloudDownload />} />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {tabValue === 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Upload New {fileType.charAt(0).toUpperCase() + fileType.slice(1)} File
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <input
                  type="file"
                  accept={fileType === 'image' ? 'image/*' : fileType === 'video' ? 'video/*' : 'audio/*'}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-upload"
                  ref={fileInputRef}
                />
                <label htmlFor="file-upload">
                  <Button variant="outlined" component="span" startIcon={<CloudUpload />}>
                    Choose File
                  </Button>
                </label>
                {selectedFile && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </Typography>
                )}
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </Box>
              {renderPreview()}
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  placeholder={`Search ${fileType} files...`}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
                <Button onClick={fetchFiles} variant="outlined" startIcon={<CloudDownload />}>
                  Refresh
                </Button>
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {filteredFiles.map(file => (
                    <ListItem key={file.key} disablePadding>
                      <ListItemButton
                        onClick={() => handleSelectFile(file.file_url)}
                        selected={currentValue === file.file_url}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>{getFileIcon()}</Box>
                        <ListItemText
                          primary={file.file_name}
                          secondary={`${formatFileSize(file.file_size)} • ${new Date(
                            file.last_modified,
                          ).toLocaleDateString()}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteFile(file.key);
                            }}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {filteredFiles.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      No {fileType} files found
                    </Typography>
                  )}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={handleClearSelection}
            disabled={!currentValue && !selectedFile}
            sx={{ mr: 'auto' }}
          >
            Clear Selection
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Success and Error Snackbars */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MediaFileSelector;
